/* ============================================================================
   assets/machina-scroll.js
   ONE scroll module for the whole theme.
   ============================================================================

   WHY THIS IS A THEME ASSET AND NOT PART OF A SECTION
   ---------------------------------------------------
   Scroll behaviour is a property of the SITE, not of a section. If two
   sections each ease the wheel, they fight over window.scrollTo. If each runs
   its own requestAnimationFrame loop, you pay for N loops and they observe
   slightly different scroll positions on the same frame, so parallax between
   sections drifts. So: one module, one loop, one set of global settings, and
   sections subscribe.

   WHAT IT PROVIDES
   ----------------
     MachinaScroll.subscribe(fn)   fn(y, dy) each frame while anything moves.
                                   Returns an unsubscribe function.
     MachinaScroll.follow(state, target, dt)
                                   Shared easing helper, so every section
                                   softens motion identically.
     MachinaScroll.wake()          Call after you change layout/geometry.
     MachinaScroll.settings        Live values, read from CSS custom properties.
     MachinaScroll.reduced         True when prefers-reduced-motion is set.
     MachinaScroll.ok              False when the browser lacks what we need.

   SETTINGS come from CSS custom properties on :root, written by
   snippets/machina-scroll-vars.liquid from the global theme settings. That
   keeps one source of truth and means a merchant edit needs no JS rebuild.

   THE LOOP STOPS. It is not an idle animation. When the scroll position is
   unchanged and every subscriber has settled, it cancels itself; a passive
   scroll listener restarts it. An idle page does zero work.

   Dependency-free. No jQuery, no GSAP, no CDN.
   ============================================================================ */
(function (w, d) {
  'use strict';

  var root = d.documentElement;

  /* ---------------------------------------------------------- capabilities */
  var OK = !!(w.CSS && CSS.supports && CSS.supports('--probe', '0')) &&
           'requestAnimationFrame' in w;

  var mq = w.matchMedia ? w.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var reduced = !!(mq && mq.matches);

  /* --------------------------------------------------------------- settings */
  var DEFAULTS = {
    /* All INTEGERS, because Shopify range settings reject values with more
       than one decimal place. Times arrive in milliseconds and factors as
       percentages; converted below. */
    '--ms-wheel-ease': 130,       /* ms  ceiling, reached on sparse input   */
    '--ms-wheel-ease-min': 30,    /* ms  floor, applied to continuous input */
    '--ms-wheel-passthru': 20,    /* ms  interval below which: adaptive = 0 */
    '--ms-follow': 0,             /* ms  artwork easing (see the note below) */
    '--ms-motion': 100,           /* %   global parallax intensity          */
    '--ms-end-zone': 640,         /* px  distance from the end where damping
                                         starts. 0 disables it entirely.    */
    '--ms-end-damp': 220          /* %   easing multiplier AT the very end   */
  };
  var settings = {};

  function num(prop) {
    var v = parseFloat(getComputedStyle(root).getPropertyValue(prop));
    return isNaN(v) ? DEFAULTS[prop] : v;
  }
  function readSettings() {
    settings.wheelEase = num('--ms-wheel-ease') / 1000;        /* ms -> s */
    settings.wheelEaseMin = num('--ms-wheel-ease-min') / 1000;  /* ms -> s */
    settings.wheelPassthru = num('--ms-wheel-passthru');        /* stays ms */
    settings.follow = num('--ms-follow') / 1000;                /* ms -> s */
    settings.motion = Math.max(0, Math.min(1, num('--ms-motion') / 100));
    settings.endZone = Math.max(0, num('--ms-end-zone'));
    settings.endDamp = Math.max(1, num('--ms-end-damp') / 100);
    settings.wheel = root.getAttribute('data-ms-wheel') !== 'off';
  }

  /* ======================================================================
     WHEEL EASING — adaptive, and why it has to be
     ======================================================================
     Easing exists for exactly one reason: to fill idle frames. So the amount
     applied is driven by the INTER-EVENT INTERVAL, not by the size of the
     delta. Input that arrives every frame has no gaps, so it passes straight
     through and costs nothing.

     Delta size is the obvious discriminator and it is wrong: a hard trackpad
     flick and a fast wheel spin both produce large sustained deltas, but only
     one of them has holes in it. Measured average lag against native:

                                   uniform 0.13s   interval-adaptive
       slow trackpad 3px/frame        22px            0px
       fast trackpad flick 22px/fr   161px            0px
       driver-smoothed 8px/frame      59px            0px
       wheel detent 100px/7fr        eased           eased (identical)

     On top of that there is a FLOOR (--ms-wheel-ease-min), because pass-through
     exposes whatever irregularity the device itself has. 0.03s costs ~4px of
     lag at trackpad speed. Raise it if a trackpad still feels steppy, drop it
     to 0 for bit-exact native.

     Scope is deliberately narrow — this is not a scroll hijack:
       · wheel events only. Keyboard, spacebar, Page Up/Down, scrollbar drag,
         touch, find-in-page and anchor jumps all stay NATIVE.
       · off entirely under prefers-reduced-motion.
       · ignores ctrl+wheel, so pinch-zoom works.
       · ignores wheel over anything scrollable (see isScrollable).
       · forces scroll-behavior:auto while active, or CSS smooth-scroll fights
         every scrollTo call.
       · resyncs on any scroll it did not cause, so the two never fight.

     KNOWN INTERACTION: a mouse driver that already synthesises its own scroll
     animation (Logitech Options+ SmoothScroll, Mac Mouse Fix, LinearMouse)
     produces input that is already an easing curve. The adaptive rule handles
     that correctly — those deltas arrive every frame, so they pass through —
     but if a device still feels wrong, raise --ms-wheel-passthru or turn the
     feature off for the site.
     ====================================================================== */
  var SW = { on: false, target: 0, raf: 0, self: 0, iv: 16, last: null, tc: 0 };

  function maxScroll() {
    return Math.max(root.scrollHeight, d.body ? d.body.scrollHeight : 0) - w.innerHeight;
  }

  function isScrollable(el) {
    while (el && el !== d.body && el.nodeType === 1) {
      if (el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true;
      if (el.hasAttribute && el.hasAttribute('data-ms-native')) return true;
      var cs = getComputedStyle(el);
      if (/(auto|scroll)/.test(cs.overflowY) && el.scrollHeight > el.clientHeight + 1) return true;
      el = el.parentNode;
    }
    return false;
  }

  function swInterval(t) {
    var gap = SW.last === null ? 1e9 : t - SW.last;
    SW.last = t;
    /* A new gesture is assumed CONTINUOUS, so the bias is always toward zero
       lag. Cost: the first detent of a wheel gesture jumps natively and easing
       ramps in over the next few. Far better than lag on every trackpad
       gesture start. */
    if (gap > 300) SW.iv = 16;
    else SW.iv += (Math.min(200, gap) - SW.iv) * 0.4;
    var adaptive = (SW.iv - settings.wheelPassthru) / 1000 * 1.6;
    SW.tc = Math.max(settings.wheelEaseMin,
                     Math.min(settings.wheelEase, Math.max(0, adaptive)));

    /* ---- deceleration into the end of the document ----------------------
       A hard flick to the bottom otherwise arrives at full speed and stops
       dead against maxScroll(). Stretching the time constant as the TARGET
       approaches the end turns that into a settle. It is applied after the
       clamp above on purpose — the clamp is about input cadence, this is
       about position, and clamping would just erase it.
       Squared so it stays out of the way until you are genuinely close. */
    if (settings.endZone > 0 && settings.endDamp > 1) {
      var end = maxScroll();
      if (end > 0) {
        var near = 1 - Math.min(1, Math.max(0, (end - SW.target) / settings.endZone));
        if (near > 0) SW.tc *= 1 + near * near * (settings.endDamp - 1);
      }
    }
  }

  function swStep() {
    if (!SW.on) { SW.raf = 0; return; }
    /* Scroll events can be coalesced, so drive the frame loop directly rather
       than relying on one firing per frame. wake() is a no-op when running. */
    wake();
    var cur = w.scrollY, gap = SW.target - cur;
    if (Math.abs(gap) < 0.5 || SW.tc <= 0.002) {
      SW.self = 1; w.scrollTo(0, SW.target); SW.raf = 0; return;
    }
    SW.self = 1;
    w.scrollTo(0, cur + gap * (1 - Math.exp(-(1 / 60) / SW.tc)));
    SW.raf = requestAnimationFrame(swStep);
  }

  function onWheel(e) {
    if (!SW.on || reduced) return;
    if (e.ctrlKey || e.deltaMode === 2) return;
    if (isScrollable(e.target)) return;
    e.preventDefault();
    swInterval(e.timeStamp || (w.performance && performance.now()) || Date.now());
    SW.target = Math.max(0, Math.min(maxScroll(),
                SW.target + e.deltaY * (e.deltaMode === 1 ? 16 : 1)));
    if (!SW.raf) SW.raf = requestAnimationFrame(swStep);
  }

  function setWheel(on) {
    SW.on = !!on && !reduced && OK;
    SW.target = w.scrollY;
    SW.last = null; SW.iv = 16; SW.tc = 0;
    root.style.scrollBehavior = SW.on ? 'auto' : '';
    if (!SW.on && SW.raf) { cancelAnimationFrame(SW.raf); SW.raf = 0; }
  }

  /* ======================================================================
     SHARED FRAME LOOP
     ====================================================================== */
  var subs = [], running = false, lastY = null, lastT = 0;

  function tick(now) {
    if (reduced || !subs.length) { running = false; return; }
    var y = w.scrollY;

    if (y === lastY) {
      var moving = false;
      for (var i = 0; i < subs.length; i++) {
        if (subs[i].busy) { moving = true; break; }
      }
      if (!moving) { running = false; return; }   /* zero work when idle */
    }

    requestAnimationFrame(tick);
    var dt = lastT ? Math.min(0.05, (now - lastT) / 1000) : 1 / 60;
    var dy = lastY === null ? 0 : y - lastY;
    lastT = now; lastY = y;

    for (var j = 0; j < subs.length; j++) {
      subs[j].busy = false;
      try { subs[j].fn(y, dy, dt, subs[j]); }
      catch (err) { if (w.console) console.error('[MachinaScroll]', err); }
    }
  }

  function wake() {
    if (running || reduced || !OK) return;
    running = true; lastY = null; lastT = 0;
    requestAnimationFrame(tick);
  }

  function subscribe(fn) {
    var rec = { fn: fn, busy: true };
    subs.push(rec);
    wake();
    return function () {
      var i = subs.indexOf(rec);
      if (i > -1) subs.splice(i, 1);
    };
  }

  /* Shared easing helper so every section softens motion identically.
     Set state.busy while it is still converging, and the loop will keep
     running until it is not. */
  function follow(state, target, dt, tc) {
    if (tc === undefined) tc = settings.follow;
    state.tgt = target;
    if (!tc || tc <= 0) { state.cur = target; return target; }
    state.cur += (target - state.cur) * (1 - Math.exp(-dt / tc));
    if (Math.abs(target - state.cur) < 0.3) state.cur = target;
    return state.cur;
  }

  /* ---------------------------------------------------------------- wiring */
  readSettings();

  if (OK) {
    w.addEventListener('wheel', onWheel, { passive: false });
    w.addEventListener('scroll', function () {
      /* Only the TARGET RESYNC has to ignore our own scrollTo. The frame loop
         must be woken either way: during an eased wheel gesture every scroll
         event is ours, so returning early here left subscribers asleep while
         the page moved — the artwork froze and only unstuck on a resize. */
      if (SW.self) SW.self = 0;
      else if (!SW.raf) SW.target = w.scrollY;
      wake();
    }, { passive: true });
    w.addEventListener('resize', function () { readSettings(); wake(); }, { passive: true });
    d.addEventListener('visibilitychange', function () { if (!d.hidden) wake(); });

    function onRM(e) {
      reduced = e.matches;
      if (reduced) setWheel(false); else setWheel(settings.wheel);
      wake();
    }
    if (mq && mq.addEventListener) mq.addEventListener('change', onRM);
    else if (mq && mq.addListener) mq.addListener(onRM);   /* Safari <= 13 */

    setWheel(settings.wheel);

    /* Theme editor rerenders a section without reloading the page. */
    d.addEventListener('shopify:section:load', function () { readSettings(); wake(); });
    d.addEventListener('shopify:section:unload', function () { wake(); });
  }

  w.MachinaScroll = {
    ok: OK,
    reduced: reduced,
    settings: settings,
    subscribe: subscribe,
    follow: follow,
    wake: wake,
    refresh: function () { readSettings(); setWheel(settings.wheel); wake(); },
    setWheel: setWheel
  };
})(window, document);
